"use client"

import { useActionState } from "react"
import type { ReactNode } from "react"

import { Alert } from "@/components/compass"

export type ActionResult =
  | { ok: true; message?: string }
  | { ok: false; formError?: string; fieldErrors?: Record<string, string> }

export type AuthActionState = ActionResult | undefined | null | void

export type AuthAction = (
  prev: AuthActionState,
  formData: FormData,
) => Promise<AuthActionState>

export type AuthFormState = {
  fieldError: (name: string) => string | undefined
  pending: boolean
}

export function useAuthForm(action: AuthAction): {
  state: AuthFormState
  formAction: (formData: FormData) => void
  banner: ReactNode | null
} {
  const [state, formAction, pending] = useActionState<AuthActionState, FormData>(
    action,
    null,
  )

  const fieldError = (name: string): string | undefined => {
    if (state && typeof state === "object" && "ok" in state && state.ok === false) {
      return state.fieldErrors?.[name]
    }
    return undefined
  }

  const formError =
    state && typeof state === "object" && "ok" in state && state.ok === false
      ? state.formError
      : undefined

  const successMessage =
    state && typeof state === "object" && "ok" in state && state.ok === true
      ? state.message
      : undefined

  const banner = formError ? (
    <Alert variant="danger">{formError}</Alert>
  ) : successMessage ? (
    <Alert variant="success">{successMessage}</Alert>
  ) : null

  return {
    state: { fieldError, pending },
    formAction,
    banner,
  }
}

export function SubmitButton({
  pending,
  label,
  pendingLabel,
}: {
  pending: boolean
  label: string
  pendingLabel: string
}) {
  return (
    <button
      type="submit"
      disabled={pending}
      aria-busy={pending || undefined}
      className="inline-flex h-10 items-center justify-center rounded-md bg-markv-primary-700 px-4 text-sm font-medium text-markv-text-50 transition-colors hover:bg-markv-primary-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-markv-primary-500 focus-visible:ring-offset-2 focus-visible:ring-offset-markv-surface-50 disabled:cursor-not-allowed disabled:opacity-50"
    >
      {pending ? pendingLabel : label}
    </button>
  )
}
